import React from 'react';
import PropTypes from 'prop-types';

import { RESUME, RESUME_NAME } from '@zr/utilities/profileFields';

import {
  STRING_FIELD_ATTACH_RESUME,
  STRING_FIELD_RESUME_RESTRICT,
  STRING_FIELD_REPLACE,
  STRING_FIELD_REMOVE,
} from '../labels';

import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_EXTENSIONS_ERROR,
  ACCEPTED_TYPES,
  MAX_FILE_SIZE,
  FILE_TOO_BIG_ERROR,
} from '../../ResumeUpload/constants';

import formikPropTypes from './formikPropTypes';

import './ApplyResumeUpload.less';

export function ApplyResumeUpload({
  formikProps: {
    errors, isSubmitting, setStatus, status, touched, values,
  },
  name: {
    resume,
    resumeName,
  },
  // this component does not manage setValues/setFieldValue
  // (because the form generally wants to handle input values very particularly and formik doesn't like setting form values in short sequence)
  // onFileAdd and onFileRemove will inform the parent form something happened
  onFileAdd,
  onFileRemove,
}) {
  const resumeNameValue = values[resumeName];
  const statusMessage = status?.[resume];
  const errorMessage = errors[resume] || statusMessage;
  // show yup errors only after touched/submitted, but show status messages whenever, cause that can include bad extension/max file size
  const showError = !!(touched[resume] && errorMessage) || statusMessage;

  const handleFileChange = (e) => {
    e.preventDefault();

    const fileList = e.target.files;

    // if file is chosen, and then second dialog is canceled and no file is
    // chosen, the handler will run with an empty file list, so abort
    if (!fileList.length) { return; }

    const [file] = e.target.files;

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      onFileAdd({
        [resumeName]: file.name,
        [resume]: reader.result,
      });
      // raise errors after loading the file into the component, so we can display the filename and the error message
      // raise error if file extension isn't one of the accepted types
      const extension = file.name.split('.').pop();
      if (!ACCEPTED_EXTENSIONS.includes(extension.toLowerCase())) {
        setStatus({
          [resume]: ACCEPTED_EXTENSIONS_ERROR,
        });
      }
      // raise error if it's over the max file size
      if (file.size >= MAX_FILE_SIZE) {
        setStatus({
          [resume]: FILE_TOO_BIG_ERROR,
        });
      }
    });

    reader.readAsDataURL(file);

    // allow the same file to be selected again
    e.target.value = null;
  };

  return (
    // the html is similar to @zr/components/ResumeUpload which is close to how this article implements it
    // https://accessabilly.com/a-styled-accessible-file-upload/
    <div className={`ApplyResumeUpload
      ${resumeNameValue ? 'picked_file' : 'empty'}
      ${showError ? 'error' : ''}
      ${isSubmitting ? 'disabled' : ''}
    `}
    >
      <input
        accept={ACCEPTED_TYPES}
        className="resume_upload_input"
        disabled={isSubmitting}
        id={resume}
        name={resume}
        onChange={handleFileChange}
        tabIndex={-1}
        type="file"
      />
      {resumeNameValue && (
        <div
          className="uploaded_resume"
        >
          <div className="file_name_text resume_icon">
            {resumeNameValue}
          </div>
          <div className="buttons_wrapper">
            <label
              htmlFor={resume}
            >
              <div
                className="resume_replace_attach_button"
                role="button"
                tabIndex={0}
              >
                {STRING_FIELD_REPLACE}
              </div>
            </label>
            <button
              className="remove_button"
              disabled={isSubmitting}
              onClick={onFileRemove}
              type="button"
            >
              {STRING_FIELD_REMOVE}
            </button>
          </div>
        </div>
      )}
      {!resumeNameValue && (
        <label
          className="resume_upload_label"
          htmlFor={resume}
        >
          <div
            className="resume_upload_attach_button"
            role="button"
            tabIndex={0}
          >
            <div className="attach_resume_text resume_icon">
              {/* space at the end so the restrict text isn't right after when read by unit test/screen reader */}
              {`${STRING_FIELD_ATTACH_RESUME} `}
            </div>
            <div className="restriction_text">
              {STRING_FIELD_RESUME_RESTRICT}
            </div>
          </div>
        </label>
      )}
      {showError && (
        <label
          className="resume_upload_error"
          htmlFor={resume}
        >
          {errorMessage}
        </label>
      )}
    </div>
  );
}

ApplyResumeUpload.propTypes = {
  formikProps: formikPropTypes.isRequired,
  name: PropTypes.shape({
    resume: PropTypes.string,
    resumeName: PropTypes.string,
  }),
  onFileAdd: PropTypes.func,
  onFileRemove: PropTypes.func,
};

ApplyResumeUpload.defaultProps = {
  // default to these constants, since some api code assumes this
  name: {
    resume: RESUME,
    resumeName: RESUME_NAME,
  },
  onFileAdd: () => {},
  onFileRemove: () => {},
};

export default ApplyResumeUpload;
